/**
 * CmsTranslationStudio.tsx
 *
 * Side-by-Side CMS Translation Studio with AI draft assistance and human review gates.
 */

import React, { useState } from 'react';
import { globalLanguageRegistryService } from '../../i18n/language-registry-service.js';
import { type TranslationRecord, type TranslationStatus } from '../../i18n/locale-contracts.js';

export const CmsTranslationStudio: React.FC = () => {
  const i18n = globalLanguageRegistryService;
  const activeLanguages = i18n.getActiveLanguages();

  const [sourceLocale, setSourceLocale] = useState<string>('en');
  const [targetLocale, setTargetLocale] = useState<string>('sw');

  const [sourceTitle, setSourceTitle] = useState<string>('Sovereign Asset Defense Overview');
  const [sourceBody, setSourceBody] = useState<string>(
    'Our decentralized sovereign mesh architecture provides autonomous continuous improvement, multi-model node orchestration, and enterprise zero-leakage security boundaries.'
  );

  const [targetTitle, setTargetTitle] = useState<string>('Muhtasari wa Ulinzi wa Rasilimali za Kujitawala');
  const [targetBody, setTargetBody] = useState<string>('');
  const [translationStatus, setTranslationStatus] = useState<TranslationStatus>('draft');
  const [translatorType, setTranslatorType] = useState<'human' | 'ai' | 'hybrid'>('human');
  const [notice, setNotice] = useState<string | null>(null);

  const handleGenerateAiDraft = () => {
    // Simulate AI Translation Assist
    if (targetLocale === 'sw') {
      setTargetTitle('Muhtasari wa Ulinzi wa Rasilimali za Kujitawala');
      setTargetBody(
        'Muundo wetu wa mtandao wa kujitawala unatoa uboreshaji endelevu unaojitegemea, uratibu wa mifano mingi ya nodi, na mipaka ya usalama ya kuzuia uvujaji wa data ya biashara.'
      );
    } else if (targetLocale === 'ar') {
      setTargetTitle('نظرة عامة على الدفاع عن الأصول السيادية');
      setTargetBody(
        'توفر بنيتنا الشبكية اللامركزية السيادية تحسينًا مستمرًا ومستقلاً، وتنسيقًا لعقد النماذج المتعددة، وحدود أمان صارمة لمنع تسرب البيانات للمؤسسات.'
      );
    } else {
      setTargetTitle(`[${targetLocale.toUpperCase()}] ${sourceTitle}`);
      setTargetBody(`[${targetLocale.toUpperCase()} Translation] ${sourceBody}`);
    }

    setTranslatorType('ai');
    setTranslationStatus('review');
    setNotice('✨ AI draft generated! Review and make necessary human edits before approving.');
  };

  const handlePublish = () => {
    if (translationStatus === 'draft') {
      setNotice('⚠️ Please review and approve the draft before publishing.');
      return;
    }

    const record: TranslationRecord = {
      contentId: `cms-page-${Date.now()}`,
      sourceLocale,
      targetLocale,
      sourceVersion: 'v1.0',
      translatedVersion: 'v1.0',
      title: targetTitle,
      body: targetBody,
      status: 'published',
      translator: translatorType,
      reviewedBy: 'principal-moderator',
      publishedAt: Date.now(),
    };

    setTranslationStatus('published');
    setNotice(`🎉 Translation published for ${targetLocale.toUpperCase()} (Reviewed by principal-moderator).`);
  };

  const targetLangDef = activeLanguages.find((l) => l.code === targetLocale);

  return (
    <div
      style={{
        padding: '24px',
        backgroundColor: 'var(--theme-bg-main, #0b0f19)',
        color: 'var(--theme-text-primary, #f8fafc)',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Studio Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 700 }}>🌐 CMS Multi-Language Translation Studio</h1>
          <p style={{ fontSize: '13px', color: 'var(--theme-text-secondary, #cbd5e1)', marginTop: '4px' }}>
            Side-by-side multilingual authoring with AI draft assistance and human review gates.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={handleGenerateAiDraft}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--theme-bg-card, #151d2a)',
              border: '1px solid var(--theme-border-highlight, #38bdf8)',
              borderRadius: 'var(--theme-radius-md, 8px)',
              color: 'var(--theme-accent, #38bdf8)',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            ⚡ Generate AI Draft
          </button>

          <button
            onClick={handlePublish}
            style={{
              padding: '8px 20px',
              backgroundColor: translationStatus === 'published' ? '#10b981' : 'var(--theme-accent, #38bdf8)',
              color: 'var(--theme-text-inverse, #0b0f19)',
              border: 'none',
              borderRadius: 'var(--theme-radius-md, 8px)',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {translationStatus === 'published' ? '✓ Published' : 'Publish Translation'}
          </button>
        </div>
      </div>

      {notice && (
        <div
          style={{
            padding: '10px 16px',
            backgroundColor: 'var(--theme-bg-card, #151d2a)',
            border: '1px solid var(--theme-border, #1e293b)',
            borderRadius: '8px',
            fontSize: '12px',
            marginBottom: '16px',
            color: 'var(--theme-text-primary, #f8fafc)',
          }}
        >
          {notice}
        </div>
      )}

      {/* Side-by-Side Editor Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', flex: 1 }}>
        {/* Left: Source Language (Readonly) */}
        <div
          style={{
            backgroundColor: 'var(--theme-bg-card, #151d2a)',
            border: '1px solid var(--theme-border, #1e293b)',
            borderRadius: 'var(--theme-radius-lg, 12px)',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', fontWeight: 600 }}>Primary Source Language</span>
            <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', backgroundColor: '#10b98122', color: '#10b981' }}>
              Master (EN)
            </span>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', marginBottom: '4px' }}>
              Title
            </label>
            <input
              type="text"
              value={sourceTitle}
              readOnly
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: 'var(--theme-bg-subtle, #020617)',
                border: '1px solid var(--theme-border, #1e293b)',
                borderRadius: '6px',
                color: 'var(--theme-text-primary, #f8fafc)',
                fontSize: '13px',
              }}
            />
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', marginBottom: '4px' }}>
              Content Body
            </label>
            <textarea
              value={sourceBody}
              readOnly
              rows={12}
              style={{
                flex: 1,
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'var(--theme-bg-subtle, #020617)',
                border: '1px solid var(--theme-border, #1e293b)',
                borderRadius: '6px',
                color: 'var(--theme-text-secondary, #cbd5e1)',
                fontSize: '12px',
                lineHeight: '1.5',
              }}
            />
          </div>
        </div>

        {/* Right: Target Language Translation (Editable) */}
        <div
          dir={targetLangDef?.direction || 'ltr'}
          style={{
            backgroundColor: 'var(--theme-bg-card, #151d2a)',
            border: '1px solid var(--theme-border-highlight, #38bdf8)',
            borderRadius: 'var(--theme-radius-lg, 12px)',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600 }}>Target Language:</span>
              <select
                value={targetLocale}
                onChange={(e) => setTargetLocale(e.target.value)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: 'var(--theme-bg-input, #1e293b)',
                  border: '1px solid var(--theme-border, #1e293b)',
                  borderRadius: '4px',
                  color: 'var(--theme-text-primary, #f8fafc)',
                  fontSize: '12px',
                }}
              >
                {activeLanguages.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.name} ({l.code.toUpperCase()})
                  </option>
                ))}
              </select>
            </div>

            <span
              style={{
                fontSize: '11px',
                padding: '2px 8px',
                borderRadius: '4px',
                backgroundColor: translationStatus === 'published' ? '#10b98122' : '#f59e0b22',
                color: translationStatus === 'published' ? '#10b981' : '#f59e0b',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              Status: {translationStatus} ({translatorType})
            </span>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', marginBottom: '4px' }}>
              Translated Title
            </label>
            <input
              type="text"
              value={targetTitle}
              onChange={(e) => {
                setTargetTitle(e.target.value);
                setTranslatorType('hybrid');
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: 'var(--theme-bg-input, #1e293b)',
                border: '1px solid var(--theme-border, #1e293b)',
                borderRadius: '6px',
                color: 'var(--theme-text-primary, #f8fafc)',
                fontSize: '13px',
              }}
            />
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', marginBottom: '4px' }}>
              Translated Content Body
            </label>
            <textarea
              value={targetBody}
              onChange={(e) => {
                setTargetBody(e.target.value);
                setTranslatorType('hybrid');
              }}
              rows={12}
              placeholder="Enter translated content or click 'Generate AI Draft' to assist..."
              style={{
                flex: 1,
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'var(--theme-bg-input, #1e293b)',
                border: '1px solid var(--theme-border, #1e293b)',
                borderRadius: '6px',
                color: 'var(--theme-text-primary, #f8fafc)',
                fontSize: '12px',
                lineHeight: '1.5',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
